import * as React from 'react';
import { cn } from '../lib/utils';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const variants = {
      primary:
        'bg-brand text-brand-navy hover:bg-brand-hover active:opacity-95 focus-visible:ring-brand',
      secondary:
        'bg-surface text-brand-navy border border-brand-navy/15 hover:bg-brand-navy hover:text-white active:bg-brand-navy',
      ghost: 'text-text hover:bg-surface-muted active:bg-surface-muted',
      destructive:
        'bg-danger text-white hover:opacity-90 active:opacity-95 focus-visible:ring-danger',
    };

    const sizes = {
      sm: 'h-9 px-3 text-[13px] rounded-sm-token gap-1.5',
      md: 'h-10 px-4 text-sm rounded-md-token gap-2',
      lg: 'h-11 px-5 text-sm rounded-md-token gap-2',
    };

    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center font-medium transition-colors transition-fast',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
          'disabled:pointer-events-none disabled:opacity-50',
          variants[variant],
          sizes[size],
          className,
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <span
            className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
            aria-hidden
          />
        )}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  size?: 'sm' | 'md';
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, label, size = 'md', children, ...props }, ref) => {
    const sizes = {
      sm: 'h-8 w-8 rounded-sm-token',
      md: 'h-9 w-9 rounded-md-token',
    };
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        title={label}
        className={cn(
          'inline-flex items-center justify-center text-text-muted transition-colors transition-fast',
          'hover:bg-surface-muted hover:text-text',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
          'disabled:pointer-events-none disabled:opacity-50',
          sizes[size],
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  },
);
IconButton.displayName = 'IconButton';
